import { type AuditLog, type AuditProps, IMPORT_PROCESS_NAME_PREFIX, type UserId } from '../model'
import { isNotDefined } from './isEmpty'

/** The oldest audit entry, written when the item was created. Pruning always keeps it. */
function getCreationLog<T extends AuditProps>(item: T): AuditLog | undefined {
  return item.modifiedBy?.reduce<AuditLog | undefined>((log, current) => {
    if (!log) return current
    if (current.timestamp < log.timestamp) return current
    return log
  }, undefined)
}

export function getCreatedAt<T extends AuditProps>(item: T): number | undefined {
  return getCreationLog(item)?.timestamp || item.createdAt // TODO: remove createdAt after migration
}

export function getCreatedBy<T extends AuditProps>(item: T): UserId | undefined {
  return getCreationLog(item)?.userId || item.createdBy // TODO: remove createdBy after migration
}

/** Whether a process name (an audit log's `updateProcess`, or `lastUpdateProcess`) belongs to an import worker */
export function isImportProcess(processName: unknown): boolean {
  if (typeof processName !== 'string') return false
  return processName.toLowerCase().includes(IMPORT_PROCESS_NAME_PREFIX.toLowerCase())
}

export function isCreatedByImport<T extends AuditProps>(item: T): boolean {
  return isImportProcess(getCreationLog(item)?.updateProcess)
}

export function getLastUpdatedBy<T extends AuditProps>(item: T): UserId | undefined {
  return (
    item.modifiedBy?.reduce<AuditLog | undefined>((log, current) => {
      if (!log) return current
      if (current.timestamp > log.timestamp) return current
      return log
    }, undefined)?.userId || item.updatedBy // TODO: remove updatedBy after migration
  )
}

export function getLastUpdatedAt<T extends AuditProps>(item: T): number | undefined {
  const modifiedAt = item.modifiedBy?.reduce<number | undefined>((updatedAt, current) => {
    if (isNotDefined(updatedAt)) return current.timestamp
    if (current.timestamp > updatedAt) return current.timestamp
    return updatedAt
  }, undefined)

  if (isNotDefined(modifiedAt)) return item.updatedAt
  if (isNotDefined(item.updatedAt)) return modifiedAt

  return Math.max(modifiedAt, item.updatedAt)
}

export function pickAuditProps<T extends AuditProps>(item: T): AuditProps {
  return {
    createdAt: getCreatedAt(item),
    createdBy: getCreatedBy(item),
    updatedAt: getLastUpdatedAt(item),
    updatedBy: getLastUpdatedBy(item),
    modifiedBy: item.modifiedBy,
  }
}
